import type { FormFieldChildrenProps, FormFieldProps } from "@/components/forms/FormField.tsx";
import { requiredMessage } from "@/components/forms/requiredMessage.tsx";
import { i18n } from "@lingui/core";
import type { NumberFieldProps, TextFieldProps } from "react-aria-components";
import type {
    ControllerFieldState,
    ControllerRenderProps,
    FieldPath,
    FieldValues,
    UseControllerProps,
} from "react-hook-form";

export function formPropsToAriaProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ field, fieldState }: { field: ControllerRenderProps<TFieldValues, TName>; fieldState: ControllerFieldState }) {
    return {
        onChange: field.onChange,
        onBlur: field.onBlur,
        value: field.value,
        isDisabled: field.disabled,

        isInvalid: fieldState.invalid,
        errorMessage: fieldState.error?.message,

        validationBehavior: "aria",
    } satisfies (TextFieldProps | NumberFieldProps) & Pick<FormFieldChildrenProps, "errorMessage">;
}

export function formPropsToFormFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ fieldState }: { field: ControllerRenderProps<TFieldValues, TName>; fieldState: ControllerFieldState }) {
    return {
        errorMessage: fieldState.error?.message,
    } satisfies FormFieldProps;
}

function controllerRules({ isRequired, required }: { isRequired?: boolean; required: string }) {
    return {
        required: isRequired ? required : undefined,
        validate: isRequired
            ? (v: unknown) => {
                  const isUnset =
                      v == null || (typeof v === "string" && v === "") || (typeof v === "number" && Number.isNaN(v));
                  return isUnset ? required : undefined;
              }
            : undefined,
    } satisfies UseControllerProps<any, any>["rules"];
}

export function controllerProps({ isRequired, isDisabled }: { isRequired?: boolean; isDisabled?: boolean }) {
    return { isDisabled, rules: controllerRules({ isRequired, required: i18n._(requiredMessage) }) };
}
